import { useRef, useState } from "react";
import { IoCameraOutline, IoImageOutline, IoAdd, IoTrashOutline } from "react-icons/io5";
import { toast } from "sonner";
import api, { fileUrl } from "@/api";
import { resizeImage } from "@/utils/imageResize";

// Single-image picker: shows preview, actions to Take Photo (env camera) or Gallery.
export const PhotoPicker = ({ fileId, imageUrl, onChange, testIdPrefix = "photo", label = "" }) => {
  const camRef = useRef(null);
  const galRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (rawFile) => {
    if (!rawFile) return;
    if (rawFile.size > 25 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 25MB)");
      return;
    }
    setUploading(true);
    // Client-side resize + compress before upload → cuts payload by 60-90%.
    const file = await resizeImage(rawFile, { maxDim: 1600, quality: 0.82 });
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.id);
    } catch {
      toast.error("Errore upload");
    } finally {
      setUploading(false);
    }
  };

  const preview = fileId ? fileUrl(fileId) : imageUrl;

  return (
    <div className={`tf-photo-picker ${preview ? "has-image" : ""} ${uploading ? "is-uploading" : ""}`} data-testid={`${testIdPrefix}-picker`}>
      {preview ? (
        <>
          <img src={preview} alt="" loading="lazy" decoding="async" />
          <div className="tf-photo-picker-actions">
            <button
              type="button"
              data-testid={`${testIdPrefix}-remove`}
              className="tf-icon-btn ghost"
              style={{ backgroundColor: "rgba(255,255,255,0.9)" }}
              onClick={(e) => { e.stopPropagation(); onChange(""); }}
              aria-label="Rimuovi"
            >
              <IoTrashOutline size={18} />
            </button>
            <button
              type="button"
              data-testid={`${testIdPrefix}-gallery-btn`}
              className="tf-icon-btn ghost"
              style={{ backgroundColor: "rgba(255,255,255,0.9)" }}
              onClick={(e) => { e.stopPropagation(); galRef.current?.click(); }}
              aria-label="Galleria"
            >
              <IoImageOutline size={18} />
            </button>
            <button
              type="button"
              data-testid={`${testIdPrefix}-camera-btn`}
              className="tf-icon-btn red"
              onClick={(e) => { e.stopPropagation(); camRef.current?.click(); }}
              aria-label="Scatta"
            >
              <IoCameraOutline size={18} />
            </button>
          </div>
        </>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: 20, textAlign: "center" }}>
          <IoAdd size={32} />
          <div style={{ fontWeight: 700, fontSize: 14 }}>{label || "Aggiungi foto"}</div>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              type="button"
              data-testid={`${testIdPrefix}-camera-btn`}
              className="tf-btn-ghost"
              onClick={() => camRef.current?.click()}
              style={{ minHeight: 40, padding: "8px 14px" }}
            >
              <IoCameraOutline size={16} /> Scatta
            </button>
            <button
              type="button"
              data-testid={`${testIdPrefix}-gallery-btn`}
              className="tf-btn-ghost"
              onClick={() => galRef.current?.click()}
              style={{ minHeight: 40, padding: "8px 14px" }}
            >
              <IoImageOutline size={16} /> Galleria
            </button>
          </div>
        </div>
      )}
      <input
        ref={camRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
      />
      <input
        ref={galRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
      />
    </div>
  );
};
